import { wait } from "@/utils";
import { useAuth } from "@/store/auth";
import environment from "@/modules/environment";
import { useStation } from "camera/store/station";

const { uploadServerUrl } = environment;
const RETRY_TIMEOUT = 3000;
const FAILURE_THRESHOLD = 4;
const UPLOAD_TIMEOUT = 2 * 60 * 1000;
const MAX_REJECTIONS = 3;

export default class Uploader {
  private static failedAttempts = 0;

  private static getTimeout = () => {
    return RETRY_TIMEOUT * this.failedAttempts;
  };

  static upload = async (
    data: FormData,
    filename: string,
    rejections = 0
  ): Promise<"OK" | "INIT_SEGMENT_NOT_FOUND" | "REJECTED"> => {
    let isRejected = false;
    try {
      const sessionToken = useAuth.getState().user?.sessionToken;
      const objectId = useStation.getState().objectId;
      if (!sessionToken || !objectId) {
        log.err(`Trying to upload without sessionToken or objectId, dropping ${filename}...:`, sessionToken, objectId);
        return "OK";
      }

      if (!data.has("sessionId")) data.append("sessionId", sessionToken!);
      if (!data.has("cameraStationId")) data.append("cameraStationId", objectId!);

      log.upload(`Trying to upload file ${filename}...`);

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT);
      const response = await fetch(uploadServerUrl, {
        method: "POST",
        body: data,
        signal: controller.signal
      });
      if (response.status >= 500 || response.status === 408 || response.status === 429) {
        throw Error(`HTTP ${response.status}`);
      }

      const { code } = (await response.json().catch(() => ({ code: `HTTP ${response.status}` }))) as {
        code: string;
        message: string;
      };
      clearTimeout(timeout);
      if (code === "OK" || code === "FILE_ALREADY_UPLOADED" || code === "INIT_FILE_ALREADY_UPLOADED") {
        log.upload(`Upload finished for ${filename}`, code);
        this.failedAttempts = 0;
        return "OK";
      } else if (code === "INIT_SEGMENT_NOT_FOUND") return "INIT_SEGMENT_NOT_FOUND";
      else if (rejections + 1 >= MAX_REJECTIONS) {
        log.err(`Upload of ${filename} refused ${MAX_REJECTIONS} times`, code);
        return "REJECTED";
      } else {
        isRejected = true;
        throw Error(code);
      }
    } catch (err: unknown) {
      log.upload(`Upload failed for file ${filename}`, err);
      const nextRejections = isRejected ? rejections + 1 : rejections;
      if (this.failedAttempts >= FAILURE_THRESHOLD) {
        log.err(`Upload failed, attempt ${this.failedAttempts}`, data);
        await wait(RETRY_TIMEOUT * 10);
        return await this.upload(data, filename, nextRejections);
      } else {
        this.failedAttempts += 1;
        log.err(`Upload failed, attempt ${this.failedAttempts}`, data);
        await wait(this.getTimeout());
        return await this.upload(data, filename, nextRejections);
      }
    }
  };
}
